/**
 * El censo, desde una hoja de cálculo · plan_captacion.md P6 · 02-11-2026
 *
 * Una federación manda el censo como Excel, y la pantalla de importar pedía JSON:
 * sembrar el directorio exigía que alguien de producto lo convirtiera a mano. Esto
 * lee el CSV que da Excel con «Guardar como» y lo deja en la forma del contrato
 * (`POST /admin/directory/import`), **en el navegador**: no hay ruta nueva, y el
 * fichero no sale de aquí hasta que se siembra.
 *
 * Lo que hace un Excel en español, y por eso está aquí:
 *
 * - **Separa con punto y coma**, porque la coma es el decimal. Se adivina por la
 *   primera línea.
 * - **Escribe `40,4168`** en las coordenadas.
 * - **Guarda en Windows-1252** si no se le pide UTF-8, y entonces «Alcalá» llega
 *   roto. Se prueba UTF-8 estricto y, si no lo es, Windows-1252.
 * - Las cabeceras las escribe una persona: «Teléfono», «Código postal», «Municipio».
 *   Se comparan sin tildes, sin mayúsculas y con varios nombres por columna.
 *
 * **Las coordenadas son opcionales.** Las pone cada noche `directory.geocode_venues`
 * (Nominatim, doscientas por noche) y la ficha se publica sola en cuanto las tiene,
 * si está completa. Una fila sin nombre se descarta; una sin disciplina, sin
 * municipio o sin forma de contacto entra, pero no saldrá en el buscador hasta
 * completarla, y se avisa.
 */

/** Un club en la forma de `ImportCensusDTO`. */
export interface ClubDelCenso {
  slug: string;
  trade_name: string;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  headline?: string | null;
  description?: string | null;
  discipline_codes?: string[];
  venue?: {
    name: string;
    address_line?: string | null;
    postal_code?: string | null;
    city?: string | null;
    province?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
}

export interface FilaLeida {
  /** El número de fila en la hoja, contando la cabecera como la 1. */
  readonly fila: number;
  readonly club: ClubDelCenso;
  /** Por qué no saldrá todavía en el buscador. Vacío si está completa. */
  readonly avisos: readonly string[];
}

export interface LecturaDelCenso {
  readonly clubes: readonly FilaLeida[];
  readonly descartadas: readonly { fila: number; motivo: string }[];
  /** Columnas que no se usan: un «Telefono 2» o una columna de notas. */
  readonly ignoradas: readonly string[];
  /** Un problema de todo el fichero, que impide leerlo. */
  readonly error?: string;
}

/** Hasta este número de clubes por petición: el tope del contrato. */
export const POR_LOTE = 500;

/** Sin tildes, sin mayúsculas, y los separadores como guion bajo. */
const normalizar = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[\s\-.]+/g, '_');

type Campo =
  | 'trade_name'
  | 'slug'
  | 'disciplinas'
  | 'phone'
  | 'email'
  | 'website'
  | 'headline'
  | 'description'
  | 'venue_name'
  | 'address_line'
  | 'postal_code'
  | 'city'
  | 'province'
  | 'latitude'
  | 'longitude';

/** Los nombres con que puede venir cada columna, ya normalizados. */
const COLUMNAS: Record<Campo, readonly string[]> = {
  trade_name: [
    'nombre',
    'club',
    'nombre_del_club',
    'nombre_comercial',
    'trade_name',
  ],
  slug: ['slug'],
  disciplinas: [
    'disciplinas',
    'disciplina',
    'discipline_codes',
    'modalidad',
    'modalidades',
  ],
  phone: ['telefono', 'tel', 'movil', 'phone'],
  email: ['email', 'correo', 'correo_electronico', 'e_mail', 'mail'],
  website: ['web', 'pagina_web', 'website', 'url'],
  headline: ['titular', 'headline'],
  description: ['descripcion', 'description'],
  venue_name: ['sede', 'nombre_de_la_sede', 'nombre_sede', 'venue_name'],
  address_line: ['direccion', 'domicilio', 'address', 'address_line'],
  postal_code: ['cp', 'codigo_postal', 'postal_code'],
  city: ['ciudad', 'municipio', 'localidad', 'poblacion', 'city'],
  province: ['provincia', 'province'],
  latitude: ['latitud', 'lat', 'latitude'],
  longitude: ['longitud', 'lng', 'lon', 'longitude'],
};

/** Las cuatro disciplinas, por cómo las escribe una federación. */
const DISCIPLINAS: Record<string, string> = {
  taekwondo: 'taekwondo',
  taekwon_do: 'taekwondo',
  tae_kwon_do: 'taekwondo',
  tkd: 'taekwondo',
  karate: 'karate',
  karate_do: 'karate',
  hapkido: 'hapkido',
  aikido: 'aikido',
};

/** Los topes del contrato, para nombrar la fila en vez de tumbar el lote. */
const TOPES: Partial<Record<Campo, number>> = {
  trade_name: 120,
  email: 200,
  phone: 30,
  website: 200,
  headline: 200,
  description: 4000,
  venue_name: 120,
  address_line: 200,
  postal_code: 10,
  city: 80,
  province: 80,
};

/** UTF-8 si lo es; si no, lo que guarda Excel en Windows. */
export function decodificar(bytes: ArrayBuffer | Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** El separador de la primera línea, fuera de comillas: `;`, `,` o tabulador. */
function separadorDe(primera: string): string {
  const cuenta: Record<string, number> = { ';': 0, ',': 0, '\t': 0 };
  let entreComillas = false;
  for (const c of primera) {
    if (c === '"') entreComillas = !entreComillas;
    else if (!entreComillas && c in cuenta) cuenta[c]! += 1;
  }
  return Object.entries(cuenta).sort((a, b) => b[1] - a[1])[0]![0];
}

/** Un CSV como lo escribe Excel: comillas dobles, comillas escapadas y saltos dentro. */
export function filasDelCsv(texto: string): string[][] {
  const limpio = texto.replace(/^\uFEFF/, '');
  const primera = limpio.split(/\r?\n/, 1)[0] ?? '';
  const sep = separadorDe(primera);

  const filas: string[][] = [];
  let fila: string[] = [];
  let celda = '';
  let entreComillas = false;

  for (let i = 0; i < limpio.length; i += 1) {
    const c = limpio[i]!;
    if (entreComillas) {
      if (c === '"' && limpio[i + 1] === '"') {
        celda += '"';
        i += 1;
      } else if (c === '"') {
        entreComillas = false;
      } else {
        celda += c;
      }
    } else if (c === '"') {
      entreComillas = true;
    } else if (c === sep) {
      fila.push(celda);
      celda = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && limpio[i + 1] === '\n') i += 1;
      fila.push(celda);
      filas.push(fila);
      fila = [];
      celda = '';
    } else {
      celda += c;
    }
  }
  if (celda !== '' || fila.length > 0) {
    fila.push(celda);
    filas.push(fila);
  }

  // Las vacías se quedan: el número de cada fila tiene que ser el de la hoja, para
  // poder encontrarla en Excel. Las salta quien las lee (`leerCenso`).
  return filas;
}

const enBlanco = (fila: readonly string[]) =>
  fila.every((c) => c.trim() === '');

/** Un `slug` válido para el contrato: minúsculas, números y guiones, 60 como mucho. */
export function slugDe(texto: string): string {
  const base = texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (base.length <= 60) return base;
  // Se corta en un guion, para no dejar media palabra.
  const corto = base.slice(0, 60);
  const ultimo = corto.lastIndexOf('-');
  return (ultimo > 20 ? corto.slice(0, ultimo) : corto).replace(/-+$/, '');
}

const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** `40,4168` o `40.4168`; `null` si está vacío; `NaN` si no es un número. */
function coordenada(texto: string): number | null {
  const t = texto.trim();
  if (t === '') return null;
  return Number(t.includes('.') ? t.replace(/,/g, '') : t.replace(',', '.'));
}

const vacio = (t: string | undefined) => (t ?? '').trim() === '';

/**
 * Lee el censo entero: qué clubes entran, cuáles se descartan y por qué.
 *
 * Los `slug` repetidos dentro del fichero se numeran (`-2`, `-3`): dos clubes con
 * el mismo nombre en el mismo municipio existen, y el segundo no puede pisar al
 * primero. Contra lo que ya hay sembrado decide el servidor: lo salta.
 */
export function leerCenso(texto: string): LecturaDelCenso {
  const filas = filasDelCsv(texto);
  if (filas.filter((f) => !enBlanco(f)).length < 2) {
    return {
      clubes: [],
      descartadas: [],
      ignoradas: [],
      error:
        'El fichero no tiene filas: hace falta la cabecera y al menos un club.',
    };
  }

  const cabecera = filas[0]!.map(normalizar);
  const indice = new Map<Campo, number>();
  const ignoradas: string[] = [];
  cabecera.forEach((nombre, i) => {
    const campo = (Object.keys(COLUMNAS) as Campo[]).find((c) =>
      COLUMNAS[c].includes(nombre)
    );
    if (campo && !indice.has(campo)) indice.set(campo, i);
    else if (nombre !== '') ignoradas.push(filas[0]![i]!.trim());
  });

  if (!indice.has('trade_name')) {
    return {
      clubes: [],
      descartadas: [],
      ignoradas,
      error:
        'Falta la columna con el nombre del club («Nombre» o «Club»). Descarga la plantilla para ver las columnas.',
    };
  }

  const usados = new Map<string, number>();
  const clubes: FilaLeida[] = [];
  const descartadas: { fila: number; motivo: string }[] = [];

  filas.slice(1).forEach((celdas, i) => {
    const fila = i + 2;
    if (enBlanco(celdas)) return;
    const v = (campo: Campo) => {
      const j = indice.get(campo);
      return j === undefined ? '' : (celdas[j] ?? '').trim();
    };
    const o = (campo: Campo) => (vacio(v(campo)) ? null : v(campo));

    const nombre = v('trade_name');
    if (nombre.length < 2) {
      descartadas.push({ fila, motivo: 'sin nombre del club' });
      return;
    }

    const largo = (Object.keys(TOPES) as Campo[]).find(
      (c) => v(c).length > TOPES[c]!
    );
    if (largo) {
      descartadas.push({
        fila,
        motivo: `${nombre}: «${largo}» pasa de ${TOPES[largo]} caracteres`,
      });
      return;
    }

    const avisos: string[] = [];

    // Disciplinas: las que reconocemos; las demás se avisan, no se inventan.
    const escritas = v('disciplinas')
      .split(/[,;/|+]|\sy\s/)
      .map((d) => normalizar(d))
      .filter(Boolean);
    const codigos = [
      ...new Set(escritas.map((d) => DISCIPLINAS[d]).filter(Boolean)),
    ] as string[];
    const desconocidas = escritas.filter((d) => !DISCIPLINAS[d]);
    if (desconocidas.length > 0) {
      avisos.push(
        `disciplina que no es de las nuestras: ${desconocidas.join(', ')}`
      );
    }
    if (codigos.length === 0) avisos.push('sin disciplina');

    const latitud = coordenada(v('latitude'));
    const longitud = coordenada(v('longitude'));
    if ((latitud === null) !== (longitud === null)) {
      descartadas.push({
        fila,
        motivo: `${nombre}: tiene latitud o longitud, pero no las dos`,
      });
      return;
    }
    if (
      latitud !== null &&
      longitud !== null &&
      (Number.isNaN(latitud) ||
        Number.isNaN(longitud) ||
        Math.abs(latitud) > 90 ||
        Math.abs(longitud) > 180)
    ) {
      descartadas.push({
        fila,
        motivo: `${nombre}: las coordenadas no son números válidos`,
      });
      return;
    }

    const ciudad = o('city');
    const conSede =
      ciudad !== null ||
      !vacio(v('address_line')) ||
      !vacio(v('postal_code')) ||
      !vacio(v('province')) ||
      latitud !== null;
    if (ciudad === null) avisos.push('sin municipio');
    if (vacio(v('phone')) && vacio(v('website'))) {
      avisos.push('sin teléfono ni web');
    }

    let slug = v('slug').toLowerCase();
    if (slug !== '' && !SLUG.test(slug)) {
      descartadas.push({
        fila,
        motivo: `${nombre}: el «slug» tiene que ser «asi-en-minusculas»`,
      });
      return;
    }
    if (slug === '') {
      const delNombre = slugDe(nombre);
      const delMunicipio = ciudad ? slugDe(ciudad) : '';
      slug =
        delMunicipio && !delNombre.endsWith(delMunicipio)
          ? slugDe(`${nombre} ${ciudad}`)
          : delNombre;
    }
    if (slug === '') {
      descartadas.push({
        fila,
        motivo: `${nombre}: no sale un «slug» de ese nombre`,
      });
      return;
    }
    const veces = (usados.get(slug) ?? 0) + 1;
    usados.set(slug, veces);
    if (veces > 1) slug = `${slug.slice(0, 56)}-${veces}`;

    clubes.push({
      fila,
      avisos,
      club: {
        slug,
        trade_name: nombre,
        email: o('email'),
        phone: o('phone'),
        website: o('website'),
        headline: o('headline'),
        description: o('description'),
        discipline_codes: codigos,
        venue: conSede
          ? {
              name: o('venue_name') ?? 'Sede principal',
              address_line: o('address_line'),
              postal_code: o('postal_code'),
              city: ciudad,
              province: o('province'),
              latitude: latitud,
              longitude: longitud,
            }
          : null,
      },
    });
  });

  return { clubes, descartadas, ignoradas };
}

/** En trozos de `tamano`, para no pasar del tope de cada petición. */
export function enLotes<T>(lista: readonly T[], tamano = POR_LOTE): T[][] {
  const lotes: T[][] = [];
  for (let i = 0; i < lista.length; i += tamano) {
    lotes.push(lista.slice(i, i + tamano));
  }
  return lotes;
}

/**
 * La plantilla que se descarga: las columnas que se entienden y dos ejemplos.
 *
 * Con punto y coma y con la marca de UTF-8 delante, que es lo que necesita Excel en
 * español para abrirla con las tildes bien sin preguntar nada.
 */
export function plantilla(): string {
  const filas = [
    [
      'Nombre',
      'Disciplinas',
      'Teléfono',
      'Email',
      'Web',
      'Sede',
      'Dirección',
      'Código postal',
      'Municipio',
      'Provincia',
      'Latitud',
      'Longitud',
    ],
    [
      'Club de Karate Ejemplo',
      'Karate',
      '910000000',
      'info@ejemplo.es',
      '',
      'Polideportivo municipal',
      'Calle Mayor, 1',
      '28801',
      'Alcalá de Henares',
      'Madrid',
      '',
      '',
    ],
    [
      'Escuela de Taekwondo Ejemplo',
      'Taekwondo, Hapkido',
      '',
      '',
      'https://ejemplo.es',
      '',
      '',
      '28001',
      'Madrid',
      'Madrid',
      '40,4168',
      '-3,7038',
    ],
  ];
  const celda = (c: string) =>
    /[;"\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c;
  return `\uFEFF${filas.map((f) => f.map(celda).join(';')).join('\r\n')}\r\n`;
}

/** Una fuente sugerida a partir del nombre del fichero: `censo_fed_madrid`. */
export function fuenteDe(nombreDeFichero: string): string {
  const base = nombreDeFichero
    .replace(/\.[^.]+$/, '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const conPrefijo = base.startsWith('censo') ? base : `censo_${base}`;
  return conPrefijo.slice(0, 40);
}
